import { ChangeDetectionStrategy, Component, inject, input, Signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslocoDirective } from '@jsverse/transloco';
import { LottieLabelComponent } from '@modules/shared/lottie-label/lottie-label.component';
import { PAGINATED_LIST_SOURCE, PaginatedListComponent } from '@modules/shared/paginated-list/paginated-list.component';
import { FlattenedTelemonitoring, Telemonitoring } from '@pixacare/dtos';
import { BasePagination } from '@shared/base-pagination';
import { MenuAction } from '@shared/models/menu-actions/menu-action';
import { map, switchMap } from 'rxjs';
import { TelemonitoringPreviewComponent } from '../telemonitoring-preview/telemonitoring-preview.component';

@Component({
  selector: 'pxc-telemonitoring-list',
  templateUrl: './telemonitoring-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    // The scroll tracker of the paginated list adds 2px on top: 6px + 2px = the 8px gap under the filter bar.
    class: 'block h-full pb-2 pt-1.5',
  },
  imports: [TelemonitoringPreviewComponent, PaginatedListComponent, TranslocoDirective, LottieLabelComponent],
})
export class TelemonitoringListComponent {
  private readonly pagination = inject<BasePagination<FlattenedTelemonitoring>>(PAGINATED_LIST_SOURCE, {
    optional: true,
  });
  private readonly router = inject(Router);

  readonly telemonitorings = input<FlattenedTelemonitoring[]>([]);
  readonly isArchived = input(false);
  readonly actions = input<MenuAction<Telemonitoring>[]>([]);
  readonly displayPatient = input(true);

  readonly collection: Signal<FlattenedTelemonitoring[]>;

  constructor() {
    const pagination = this.pagination;
    if (pagination) {
      const collection$ = toObservable(this.isArchived).pipe(
        switchMap((isArchived) => pagination.select({ isArchived }).pipe(map((collection) => collection.data))),
      );
      this.collection = toSignal(collection$, { initialValue: [] });
    } else {
      this.collection = this.telemonitorings;
    }
  }

  redirect(telemonitoring: Telemonitoring): void {
    this.router.navigate(['/client', telemonitoring.clientCode, 'telemonitorings', telemonitoring.id]);
  }
}
